import React, { useState } from 'react';
import {
    Table, Card, Button, Space, Tag, Typography, Layout,
    Modal, Form, Input, Menu, Empty, Badge, Result, message, Popconfirm
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import CompileRoleSwitcher, { getCompileRole, setCompileRole } from '../CompileRole';

const { Title, Text } = Typography;
const { Sider, Content } = Layout;

const STATUS_MAP = {
    online: { text: '上线', color: 'success' },
    offline: { text: '已下线', color: 'default' },
};

// TODO: 联调时替换为接口数据
const MOCK_IOT_CATEGORIES = [
    { id: 'cat_1', name: '摄像机' },
    { id: 'cat_2', name: 'HUB类设备' },
    { id: 'cat_3', name: '4G设备' },
    { id: 'cat_4', name: 'NVR设备' },
];

// TODO: 联调时替换为接口数据
// SDK版本不在此维护，由发起编译时动态拉取
const MOCK_SDK_SERVICES = {
    'cat_1': [
        { id: 'svc_001', name: '单目', repoUrl: 'https://git.hmy.com/sdk/ipc-mono.git', description: '单目摄像机SDK服务', status: 'online' },
        { id: 'svc_002', name: '双目', repoUrl: 'https://git.hmy.com/sdk/ipc-dual.git', description: '双目摄像机SDK服务', status: 'online' },
        { id: 'svc_003', name: '全景', repoUrl: 'https://git.hmy.com/sdk/ipc-pano.git', description: '全景摄像机SDK服务', status: 'offline' },
    ],
    'cat_2': [],
    'cat_3': [
        { id: 'svc_004', name: '4G低功耗', repoUrl: 'https://git.hmy.com/sdk/4g-lowpower.git', description: '4G低功耗设备SDK', status: 'online' },
    ],
    'cat_4': [
        { id: 'svc_005', name: '标准NVR', repoUrl: 'https://git.hmy.com/sdk/nvr-std.git', description: '标准NVR设备SDK', status: 'online' },
    ],
};

// ══════════════════════════════════════════════
//  子组件：新增/编辑SDK服务弹窗
// ══════════════════════════════════════════════

const ServiceModal = ({ open, record, categoryName, onClose }) => {
    const [form] = Form.useForm();
    const isEdit = !!record;

    React.useEffect(() => {
        if (open && record) {
            form.setFieldsValue({ name: record.name, repoUrl: record.repoUrl, description: record.description });
        }
    }, [open, record, form]);

    const handleOk = () => {
        form.validateFields().then(() => {
            message.success(isEdit ? 'SDK服务已更新' : 'SDK服务已创建');
            form.resetFields();
            onClose();
        });
    };

    return (
        <Modal
            title={isEdit ? `编辑SDK服务 - ${categoryName}` : `新增SDK服务 - ${categoryName}`}
            open={open}
            onCancel={() => { form.resetFields(); onClose(); }}
            onOk={handleOk}
            okText="保存"
            width={520}
            destroyOnClose
        >
            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item label="服务名称" name="name" rules={[{ required: true, message: '请输入服务名称' }]}>
                    <Input placeholder="如 单目、双目、标准NVR" />
                </Form.Item>
                <Form.Item label="仓库地址" name="repoUrl" rules={[{ required: true, message: '请输入仓库地址' }]}>
                    <Input placeholder="如 https://git.hmy.com/sdk/xxx.git" />
                </Form.Item>
                <Form.Item label="描述" name="description">
                    <Input.TextArea rows={3} placeholder="服务用途说明（选填）" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

// ══════════════════════════════════════════════
//  主组件：SDK服务管理
// ══════════════════════════════════════════════

const SdkVersion = () => {
    const [role, setRole] = useState(getCompileRole());
    const isPlatform = role === 'platform';

    const [selectedCategory, setSelectedCategory] = useState(MOCK_IOT_CATEGORIES[0]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);

    const currentServices = MOCK_SDK_SERVICES[selectedCategory?.id] || [];

    const handleRoleChange = (next) => {
        setCompileRole(next); // 模块内共用，切换后其它页面保持一致
        setRole(next);
    };

    const handleToggle = (record) => {
        const action = record.status === 'online' ? '下线' : '上线';
        message.success(`已${action}：${record.name}`);
    };

    // 左侧类目菜单
    const categoryMenuItems = MOCK_IOT_CATEGORIES.map(c => {
        const count = (MOCK_SDK_SERVICES[c.id] || []).length;
        return {
            key: c.id,
            label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{c.name}</span>
                    <Badge count={count} style={{ backgroundColor: '#e6f4ff', color: '#1677ff', boxShadow: 'none' }} showZero />
                </div>
            ),
        };
    });

    const columns = [
        { title: '服务名称', dataIndex: 'name', width: 120, render: (text) => <Text strong>{text}</Text> },
        {
            title: '仓库地址', dataIndex: 'repoUrl', width: 280,
            render: (text) => <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>{text}</Text>,
        },
        { title: '描述', dataIndex: 'description', render: (text) => text || <span style={{ color: '#ccc' }}>-</span> },
        {
            title: '状态', dataIndex: 'status', width: 80,
            render: (s) => <Tag color={STATUS_MAP[s]?.color} style={{ border: 'none' }}>{STATUS_MAP[s]?.text}</Tag>,
        },
        {
            title: '操作', key: 'action', width: 150, fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditRecord(record); setModalOpen(true); }}>编辑</Button>
                    {record.status === 'online' ? (
                        <Popconfirm title="下线后编译任务不可选择该服务，确定？" onConfirm={() => handleToggle(record)}>
                            <Button type="link" size="small" danger>下线</Button>
                        </Popconfirm>
                    ) : (
                        <Button type="link" size="small" onClick={() => handleToggle(record)}>上线</Button>
                    )}
                </Space>
            ),
        },
    ];

    // 企业角色：SDK版本管理为平台角色专属功能，无权限访问
    if (!isPlatform) {
        return (
            <div style={{ padding: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={4} style={{ margin: 0 }}>SDK版本管理</Title>
                    <CompileRoleSwitcher role={role} onChange={handleRoleChange} />
                </div>
                <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                    <Result
                        status="403"
                        title="无访问权限"
                        subTitle="「SDK版本管理」仅平台角色可访问，请切换到平台角色查看。"
                    />
                </Card>
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Space align="baseline" size={8}>
                <Title level={4} style={{ margin: 0 }}>SDK版本管理</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>SDK版本在发起编译时动态获取，此处不维护</Text>
            </Space>
            <CompileRoleSwitcher role={role} onChange={handleRoleChange} />
        </div>
        <Layout style={{ minHeight: 'calc(100vh - 180px)', background: '#fff', border: '1px solid #f0f0f0', borderRadius: 12, overflow: 'hidden' }}>
            <Sider width={240} style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}>
                <div style={{ padding: '16px 16px 8px' }}>
                    <Title level={5} style={{ margin: 0 }}>IOT类目</Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>选择类目查看SDK服务</Text>
                </div>
                {MOCK_IOT_CATEGORIES.length > 0 ? (
                    <Menu
                        mode="inline"
                        selectedKeys={[selectedCategory?.id]}
                        items={categoryMenuItems}
                        onClick={({ key }) => setSelectedCategory(MOCK_IOT_CATEGORIES.find(c => c.id === key))}
                        style={{ borderRight: 'none' }}
                    />
                ) : (
                    <Empty description="暂无IOT类目" style={{ marginTop: 40 }} />
                )}
            </Sider>
            <Content style={{ padding: 24 }}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={4} style={{ margin: 0 }}>
                        {selectedCategory?.name} - SDK服务
                    </Title>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditRecord(null); setModalOpen(true); }}>新增</Button>
                </div>

                {currentServices.length > 0 ? (
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <Table
                            dataSource={currentServices}
                            columns={columns}
                            rowKey="id"
                            scroll={{ x: 800 }}
                            pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
                        />
                    </Card>
                ) : (
                    <Card bordered={false} style={{ borderRadius: 12 }}>
                        <Empty description="该类目下暂无SDK服务" />
                    </Card>
                )}
            </Content>

            <ServiceModal
                open={modalOpen}
                record={editRecord}
                categoryName={selectedCategory?.name}
                onClose={() => { setModalOpen(false); setEditRecord(null); }}
            />
        </Layout>
        </div>
    );
};

export default SdkVersion;
